import { observer } from 'mobx-react-lite'
import { AppPaper } from 'src/component/common/analog/molecule/AppPaper'
import { Avatar, Fade, Tooltip, Typography } from '@mui/material'
import { useCallback } from 'react'
import { AppSkeleton } from 'src/component/common/analog/molecule/AppSkelton'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { ColorTokens, Palette, RadiusTokens } from 'src/feature/theme/model/Palette'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { TeamRolesUsersSkillGapMetrics } from '../../model/my-team-roles'
import { API_BASE_URL } from 'src/util/constant'
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded'
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded'
import StarRoundedIcon from '@mui/icons-material/StarRounded'
import { getCardRatingColor, getRatingLevelLabel } from '../../util/skillGapUtils'

export type TeamRolesUsersListCardProps = {
  user?: TeamRolesUsersSkillGapMetrics
  isLoading: boolean
  loadingCount?: number
  onOpenDetails?: (user: TeamRolesUsersSkillGapMetrics) => void
}

const loadingSkeletonRects = [{ width: '100%', height: 220, color: 'background.dark' }]
const highestRatedIconSx = { fontSize: 12, color: Palette.OLIVE_GREEN }
const lowestRatedIconSx = { fontSize: 12, color: Palette.DANGER }
const ratingStarSx = { fontSize: 12, color: ColorTokens.yellow200 }
const absoluteUrlPattern = /^(?:https?:)?\/\//i

const getUserInitials = (name: string) => {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase() ?? '')
    .join('')

  return initials || name.slice(0, 2).toUpperCase()
}

const resolveAvatarSrc = (avatarUrl?: string) => {
  if (!avatarUrl) {
    return undefined
  }

  if (absoluteUrlPattern.test(avatarUrl) || avatarUrl.startsWith('data:') || avatarUrl.startsWith('blob:')) {
    return avatarUrl
  }

  const normalizedBase = API_BASE_URL.replace(/\/+$/, '')
  const normalizedPath = avatarUrl.replace(/^\/+/, '')

  return `${normalizedBase}/${normalizedPath}`
}

const formatCreatedAt = (createdAt?: string) => {
  if (!createdAt) {
    return '-'
  }

  const parsedDate = new Date(createdAt)

  return Number.isNaN(parsedDate.getTime()) ? '-' : parsedDate.toLocaleDateString()
}

export const TeamRolesUsersListCard = observer(({ user, isLoading, loadingCount = 1, onOpenDetails }: TeamRolesUsersListCardProps) => {
  const { translation } = useLocalizationStore()

  if (isLoading || !user) {
    return (
      <AppSkeleton
        count={loadingCount}
        direction='row'
        rects={loadingSkeletonRects}
      />
    )
  }

  const pageTranslation = translation.pages.myTeam
  const currentRatingText = user.avgSkillRating.toFixed(1)
  const ratingBadgeBackground = getCardRatingColor(user.avgSkillRating)
  const ratingLevel = getRatingLevelLabel(user.avgSkillRating)
  const initials = getUserInitials(user.name)
  const avatarSrc = resolveAvatarSrc(user.avatarUrl)
  const joinedAt = formatCreatedAt(user.createdAt)
  const levelBadgeSx = { ...styles.levelBadge, backgroundColor: ratingBadgeBackground }

  const handleShowMoreClick = useCallback(() => {
    onOpenDetails?.(user)
  }, [onOpenDetails, user])

  const skillHighlights = [
    {
      label: pageTranslation.metricsHeader.highestRated(),
      value: user.highestRatedSkill || '-',
      icon: <ArrowUpwardRoundedIcon sx={highestRatedIconSx} />,
    },
    {
      label: pageTranslation.metricsHeader.lowestRated(),
      value: user.lowestRatedSkill || '-',
      icon: <ArrowDownwardRoundedIcon sx={lowestRatedIconSx} />,
    },
  ]

  return (
    <Fade in={true} timeout={500} mountOnEnter unmountOnExit>
      <AppPaper sx={styles.root} onClick={handleShowMoreClick}>
        <AppBox sx={styles.leftColumn}>
          <Avatar src={avatarSrc} sx={styles.avatar}>{initials}</Avatar>
          <AppBox sx={styles.userInfoColumn}>
            <Typography variant='h6' sx={styles.userName} noWrap>{user.name}</Typography>
            <Typography sx={styles.userMetaText} noWrap>{user.email}</Typography>
            <Typography sx={styles.userMetaText} noWrap>
              {`${translation.pages.profileInfo.profileCard.memberSince()}: ${joinedAt}`}
            </Typography>

            <AppBox sx={styles.skillHighlightsRow}>
              {skillHighlights.map(item => (
                <AppBox key={item.label} sx={styles.skillPill}>
                  {item.icon}
                  <Tooltip title={`${item.label}: ${item.value}`} arrow>
                    <Typography sx={styles.skillPillText} noWrap>{item.value}</Typography>
                  </Tooltip>
                </AppBox>
              ))}
            </AppBox>
          </AppBox>
        </AppBox>

        <AppBox sx={styles.rightColumn}>
          <AppBox sx={styles.ratingValueRow}>
            <StarRoundedIcon sx={ratingStarSx} />
            <Typography sx={styles.ratingValue}>{currentRatingText}</Typography>
          </AppBox>
          <Typography sx={levelBadgeSx}>
            {ratingLevel}
          </Typography>
        </AppBox>
      </AppPaper>
    </Fade>
  )
})

const styles = {
  root: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    padding: '5px 10px',
    borderRadius: '10px',
    border: '1px solid',
    borderColor: 'transparent',
    width: '100%',
    minWidth: 0,
    cursor: 'pointer',
    transition: 'border-color 0.2s ease-in-out',
    '&:hover': {
      borderColor: ColorTokens.border100,
    },
  },
  leftColumn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 20,
    minWidth: 0,
    flex: 1,
  },
  avatar: {
    width: 50,
    height: 50,
    flexShrink: 0,
  },
  userInfoColumn: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  userName: {
    maxWidth: '28em',
    lineHeight: 1.2,
  },
  userMetaText: {
    color: 'text.secondary',
    fontSize: 12,
    lineHeight: 1.2,
    minWidth: 0,
    maxWidth: '100%',
  },
  rightColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 5,
    flexShrink: 0,
    width: 132,
  },
  ratingValueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    width: '100%',
  },
  ratingValue: {
    fontSize: 12,
    color: 'text.primary',
  },
  levelBadge: {
    borderRadius: RadiusTokens.pill,
    padding: '5px 10px',
    minWidth: 110,
    fontSize: 12,
    lineHeight: 1.2,
    color: 'background.default',
    textAlign: 'center',
  },
  skillHighlightsRow: {
    width: '100%',
    flexDirection: 'row',
    gap: 10,
    flexWrap: 'wrap',
  },
  skillPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    backgroundColor: ColorTokens.secondary500,
    color: 'background.default',
    borderRadius: RadiusTokens.pill,
    padding: '2px 6px',
    maxWidth: { xs: '100%', md: 160 },
    minWidth: 0,
  },
  skillPillText: {
    color: 'inherit',
    fontSize: 12,
    minWidth: 0,
  },
}
